import React, { useState, useMemo } from "react";
import toast from "react-hot-toast";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  useDisclosure,
  Spinner,
} from "@chakra-ui/react";
import {
  Heart,
  SnapReact,
  FistReact,
  ClappingReact,
  HeartReact,
} from "../../Assets/SvgIcons";
import {
  BoxReact,
  ContainerReact,
  ContainerEmotion,
  BoxCountEmotion,
  CountEmotion,
} from "./styles";

import { useAddInteractionsToPostMutation } from "../../../redux/reducers/apiSlice";
import { INTERACTION_ICONS } from "../../utils/constants";
import { usePost } from "../../hooks/usePost";
import * as Sentry from "@sentry/react";

export const Reactions = ({ post }) => {
  const { currentUserHasInteracted } = usePost(post);

  const [addInteractionsToPost, { isLoading: addInteractionsToPostLoading }] =
    useAddInteractionsToPostMutation();
  const [pendingType, setPendingType] = useState<string | null>(null);
  const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout>>();
  const { isOpen, onToggle, onOpen, onClose } = useDisclosure();

  const likeUnlikeAction = async (interactionType: string) => {
    if (addInteractionsToPostLoading || pendingType) return;

    setPendingType(interactionType);
    try {
      await addInteractionsToPost({
        type: interactionType,
        id: post?.id,
      }).unwrap();
      onClose();
    } catch (e: any) {
      Sentry.captureException(e);
      const message =
        e?.data?.message || e?.error || "Error while interacting with post";
      toast.error(message);
    } finally {
      setPendingType(null);
    }
  };

  const handleMouseLeave = () => {
    const newTimeoutId = setTimeout(() => {
      onClose();
    }, 2000);
    setTimeoutId(newTimeoutId);
  };

  const handleMouseOver = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    onOpen();
  };

  const interactions = useMemo(() => {
    return (post?.interactionCount ?? [])
      .filter((interaction) => interaction.type !== "COMMENT")
      .sort((a, b) => a.type.localeCompare(b.type))
      .map((interaction) => {
        const Interaction = INTERACTION_ICONS[interaction.type];
        const isPending = pendingType === interaction.type;
        return (
          <BoxCountEmotion
            key={interaction.type}
            onClick={() => likeUnlikeAction(interaction.type)}
          >
            <Interaction />
            {isPending ? (
              <Spinner size="xl" color="red" />
            ) : (
              <CountEmotion>{interaction.count}</CountEmotion>
            )}
          </BoxCountEmotion>
        );
      });
  }, [post?.interactionCount, pendingType]);

  const isHeartPending = pendingType === "HEART";

  return (
    <>
      <button className="icon" type="button">
        <Popover
          trigger="hover"
          isLazy
          isOpen={isOpen}
          returnFocusOnClose={false}
          placement={"top-start"}
        >
          <PopoverTrigger>
            <div
              style={{ cursor: "pointer" }}
              onMouseEnter={onToggle}
              onClick={() => likeUnlikeAction("HEART")}
              onMouseLeave={handleMouseLeave}
            >
              {isHeartPending ? (
                <Spinner size="sm" color="#EE5A29" />
              ) : (
                <Heart color={currentUserHasInteracted} />
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent
            onMouseLeave={handleMouseLeave}
            onMouseOver={handleMouseOver}
            width="160px"
          >
            <BoxReact>
              <ContainerReact
                onMouseLeave={handleMouseLeave}
                onMouseOver={handleMouseOver}
                onClick={() => likeUnlikeAction("SNAPS")}
              >
                <SnapReact />
              </ContainerReact>
              <ContainerReact
                onMouseLeave={handleMouseLeave}
                onMouseOver={handleMouseOver}
                onClick={() => likeUnlikeAction("FIST")}
              >
                <FistReact />
              </ContainerReact>
              <ContainerReact
                onMouseLeave={handleMouseLeave}
                onMouseOver={handleMouseOver}
                onClick={() => likeUnlikeAction("HIGHFIVE")}
              >
                <ClappingReact />
              </ContainerReact>
              <ContainerReact
                onMouseLeave={handleMouseLeave}
                onMouseOver={handleMouseOver}
                onClick={() => likeUnlikeAction("HEART")}
              >
                <HeartReact />
              </ContainerReact>
            </BoxReact>
          </PopoverContent>
        </Popover>
      </button>
      <ContainerEmotion>{interactions}</ContainerEmotion>
    </>
  );
};
